import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  fetchUsageReportCsv,
  useUsageReport,
  type Granularity,
  type UsageReportBucket,
} from '@/billing/api'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

// The numeric columns of a report bucket / totals row (everything but `period`).
const METRIC_COLUMNS = [
  'deck_view',
  'proactive_like',
  'new_conversation',
  'message_in_conversation',
  'active_users',
  'new_users',
  'new_matches',
] as const

/**
 * `/billing-reports` — admin viselkedés-riport (DEC-BILLING-007). Konfigurálható
 * intervallum (kezdő/záró dátum + nap/hét/hó bontás) + CSV-letöltés. Operations
 * surface. Az adat a backend usage_counters-éből aggregálódik (a korlátozás
 * flag-jétől függetlenül gyűlik).
 */
export function UsageReport() {
  const { t } = useTranslation()

  const today = new Date()
  const monthAgo = new Date()
  monthAgo.setDate(today.getDate() - 30)

  const [from, setFrom] = useState(isoDate(monthAgo))
  const [to, setTo] = useState(isoDate(today))
  const [granularity, setGranularity] = useState<Granularity>('day')
  const [downloading, setDownloading] = useState(false)

  const params = { from, to, granularity }
  const report = useUsageReport(params)

  async function onDownloadCsv() {
    setDownloading(true)
    try {
      const csv = await fetchUsageReportCsv(params)
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `usage-report-${from}_${to}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  const buckets = report.data?.buckets ?? []
  const totals = report.data?.totals

  return (
    <main className="mx-auto flex max-w-5xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('billingReports.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('billingReports.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('billingReports.intro')}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('billingReports.from')}</span>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded border px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('billingReports.to')}</span>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="rounded border px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('billingReports.granularity')}</span>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity)}
            className="rounded border px-2 py-1"
          >
            <option value="day">{t('billingReports.day')}</option>
            <option value="week">{t('billingReports.week')}</option>
            <option value="month">{t('billingReports.month')}</option>
          </select>
        </label>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void onDownloadCsv()}
          disabled={downloading || report.isPending}
        >
          {t('billingReports.downloadCsv')}
        </Button>
      </div>

      {report.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {report.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('billingReports.loadError')}</AlertDescription>
        </Alert>
      )}

      {!report.isPending && !report.isError && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="p-2">{t('billingReports.period')}</th>
                {METRIC_COLUMNS.map((col) => (
                  <th key={col} className="p-2 text-right">
                    {t(`billingReports.metric.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets.length === 0 && (
                <tr>
                  <td
                    colSpan={METRIC_COLUMNS.length + 1}
                    className="p-4 text-center text-muted-foreground"
                  >
                    {t('billingReports.empty')}
                  </td>
                </tr>
              )}
              {buckets.map((bucket: UsageReportBucket) => (
                <tr key={bucket.period} className="border-b">
                  <td className="p-2 font-mono">{bucket.period}</td>
                  {METRIC_COLUMNS.map((col) => (
                    <td key={col} className="p-2 text-right">
                      {bucket[col]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td className="p-2">{t('billingReports.totals')}</td>
                  {METRIC_COLUMNS.map((col) => (
                    <td key={col} className="p-2 text-right">
                      {totals[col]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </main>
  )
}
